/**
 * contentTemplates: code-defined starter templates for the content editor.
 *
 * P5 wave 3b (content-editor starter templates) of the multi-tenancy refactor.
 *
 * Pages on RILE Connect are fully block-composed: a `landing` / `about` /
 * `mission` / `contact` page is a free arrangement of typed blocks the admin
 * places and orders. To avoid a blank-page start, the editor seeds a single pre-arranged
 * template per page when the admin clicks "Use Template". There is exactly one
 * template per page key.
 *
 * Each template's blocks carry ecosystem-NEUTRAL placeholder content: genuine
 * scaffolding that guides the author ("Describe your ecosystem's mission
 * here.") rather than lorem ipsum, and never anything COSTEM-specific.
 * Templates are shared across every tenant.
 *
 * Block `data` shapes mirror `blockTypes.js` (the P5-3a source of truth for
 * each type's default `data`) and the P5-2 renderer's block contract exactly.
 * The 16 block types and their `data`:
 *   - hero            { heading, subheading?, backgroundImageUrl?,
 *                       ctaText?, ctaLink?,
 *                       secondaryCtaText?, secondaryCtaLink?,
 *                       audienceItems?: [{ label, description }],
 *                       showGraphPreview? }
 *   - heading         { text, level }            (level 2 or 3)
 *   - paragraph       { html }                   (rich-text HTML)
 *   - image           { url, alt, caption? }
 *   - image-grid      { images: [{ url, alt, caption? }] }
 *   - stat            { items: [{ value, label }] }
 *   - quote           { text, attribution? }
 *   - cta-button      { text, link, variant? }
 *   - live-stats      { sectionHeading?, visibleStats? }
 *   - logo-text       { imageUrl?, imageAlt?, heading?, body?, buttonText?, buttonLink? }
 *   - ecosystem-join  { heading?, body?, buttons?: [{ text, link, variant }] }
 *   - ecosystem-orgs  { limit? }
 *   - recent-orgs     { limit? }
 *   - ecosystem-stats {}
 *   - graph-preview   {}
 *   - capability-cards  { sectionHeading?, hiddenCards? }
 *
 * `order` in the template definitions is authored contiguously (0..n-1);
 * `getTemplateBlocks` re-sequences defensively and deep-clones on every read so
 * applying a template can never share object references with (and therefore
 * never mutate) the template definition.
 */
import { newBlockClientId } from './blockTypes';

/**
 * One starter template per page. Each template's `blocks` are authored
 * `{ type, data, order }` tuples; consume them via `getTemplateBlocks`, never
 * the raw `blocks` array (which must not be mutated).
 *
 * @type {Array<{id: string, name: string, description: string,
 *   pageKey: 'landing'|'about'|'mission'|'contact',
 *   blocks: Array<{type: string, data: object, order: number}>}>}
 */
export const CONTENT_TEMPLATES = [
    // --- landing -----------------------------------------------------------
    {
        id: 'landing-full',
        name: 'Full Ecosystem Landing',
        description:
            'A complete landing page: hero banner, an introduction, live ' +
            'featured organizations and ecosystem stats, and a closing ' +
            'call-to-action.',
        pageKey: 'landing',
        blocks: [
            {
                type: 'hero',
                data: {
                    heading: 'Welcome to your ecosystem',
                    subheading:
                        'A one-line summary of who you bring together and why.',
                    backgroundImageUrl: '',
                    ctaText: 'Explore the directory',
                    ctaLink: '/directory',
                    showGraphPreview: true,
                },
                order: 0,
            },
            {
                type: 'paragraph',
                data: {
                    html: '<p>Introduce your ecosystem here. Describe the community you serve, the region or focus area you cover, and what a visitor can do on this site.</p>',
                },
                order: 1,
            },
            {
                type: 'capability-cards',
                data: {
                    hiddenCards: [
                        'discoverPartners',
                        'networkGraph',
                        'ecosystemAnalytics',
                    ],
                    sectionHeading: 'What You Can Do Here',
                },
                order: 2,
            },
            {
                type: 'logo-text',
                data: {
                    imageUrl: 'https://picsum.photos/400/300',
                    imageAlt: '',
                    heading: 'Our Mission',
                    body: 'Consider displaying your mission statement here or just a bit more about your ecosystem. Maybe replace the image with your ecosystem logo?',
                    buttonText: '',
                    buttonLink: '',
                },
                order: 3,
            },
            { type: 'ecosystem-orgs', data: { limit: 9 }, order: 4 },
            { type: 'ecosystem-stats', order: 5 },
            {
                type: 'ecosystem-join',
                data: {
                    heading: 'Get Involved',
                    body: '',
                    buttons: [
                        {
                            text: 'Create an Account',
                            link: '/signup',
                            variant: 'accent',
                        },
                    ],
                },
                order: 6,
            },
        ],
    },
    // --- about -------------------------------------------------------------
    {
        id: 'about-standard',
        name: 'Full About',
        description:
            'An about page: a hero banner, descriptive paragraphs about ' +
            'your ecosystem and the work it supports, a pull quote, and a ' +
            'representative image.',
        pageKey: 'about',
        blocks: [
            {
                type: 'hero',
                data: {
                    heading: 'About us',
                    subheading: 'Who we are and what we do.',
                    backgroundImageUrl: '',
                    ctaText: '',
                    ctaLink: '',
                },
                order: 0,
            },
            {
                type: 'paragraph',
                data: {
                    html:
                        '<p>Describe your ecosystem in detail: how it started, ' +
                        'who it serves, and the change it works toward. ' +
                        'This is the place for the longer story.</p>' +
                        '<p>Think about adding quotes and images to make ' +
                        'your story engaging!</p>',
                },
                order: 1,
            },
            {
                type: 'heading',
                data: { text: 'What we do', level: 2 },
                order: 2,
            },
            {
                type: 'paragraph',
                data: {
                    html: '<p>Outline the programs, partnerships, or activities your ecosystem coordinates. Use a list if that reads more clearly.</p>',
                },
                order: 3,
            },
            {
                type: 'quote',
                data: {
                    text: 'Add a quote that captures your ecosystem in a partner or member’s own words.',
                    attribution: 'Name, Role or Organization',
                },
                order: 4,
            },
            {
                type: 'image',
                data: {
                    url: 'https://picsum.photos/800/600',
                    alt: 'A representative image of your ecosystem',
                    caption: 'Add a caption that gives this image context.',
                },
                order: 5,
            },
            {
                type: 'ecosystem-join',
                data: {
                    heading: 'How to get involved',
                    body: '',
                    buttons: [
                        {
                            text: 'Join the Ecosystem',
                            link: '/signup',
                            variant: 'accent',
                        },
                        {
                            text: 'Browse Organizations',
                            link: '/directory',
                            variant: 'outline-light',
                        },
                    ],
                },
                order: 6,
            },
        ],
    },
    // --- mission -----------------------------------------------------------
    {
        id: 'mission-with-values',
        name: 'Full Mission and Values',
        description:
            'A mission page that pairs the mission statement with the ' +
            'values that guide your ecosystem.',
        pageKey: 'mission',
        blocks: [
            {
                type: 'hero',
                data: {
                    heading: 'Our Mission',
                    subheading: `"Your ecosystem's slogan or phrase"`,
                    backgroundImageUrl: '',
                    ctaText: '',
                    ctaLink: '',
                    secondaryCtaText: '',
                    secondaryCtaLink: '',
                    audienceItems: [],
                    showGraphPreview: false,
                },
                order: 0,
            },
            {
                type: 'logo-text',
                data: {
                    imageUrl: 'https://picsum.photos/400/300',
                    imageAlt: '',
                    heading: '',
                    body: 'State your ecosystem’s mission in one clear, compelling paragraph. Then replace the image with your logo.',
                    buttonText: '',
                    buttonLink: '',
                },
                order: 1,
            },
            {
                type: 'paragraph',
                data: {
                    html: '<p>Need more space for your mission statement? Then use this text block to continue telling your story.</p>',
                },
                order: 2,
            },
            {
                type: 'heading',
                data: { text: 'Our values', level: 3 },
                order: 3,
            },
            {
                type: 'paragraph',
                data: {
                    html:
                        '<p>Describe the values that guide your ecosystem. ' +
                        'Then use a list to highlight specific values.</p>' +
                        '<ul>' +
                        '<li><p><strong>Value 1 - </strong>Why is this value important to you?</p></li>' +
                        '<li><p><strong>Value 2 - </strong>Why is this value important to you?</p></li>' +
                        '<li><p><strong>Value 3 - </strong>Why is this value important to you?</p></li>' +
                        '<li><p><strong>Value 4 - </strong>Why is this value important to you?</p></li>' +
                        '</ul>',
                },
                order: 4,
            },
            {
                type: 'quote',
                data: {
                    text: 'Add a quote that ties your mission and values together.',
                    attribution: 'Name, Role or Organization',
                },
                order: 5,
            },
        ],
    },
    // --- contact -------------------------------------------------------------
    {
        id: 'contact-standard',
        name: 'Full Contact',
        description:
            'A contact page: a hero banner, ways to reach your ecosystem, ' +
            'and a call-to-action link.',
        pageKey: 'contact',
        blocks: [
            {
                type: 'hero',
                data: {
                    heading: 'Get in Touch',
                    subheading:
                        'How to reach your ecosystem and where to learn more.',
                    backgroundImageUrl: '',
                    ctaText: '',
                    ctaLink: '',
                },
                order: 0,
            },
            {
                type: 'paragraph',
                data: {
                    html:
                        '<p>Share the best way to reach your ecosystem: an email address, ' +
                        'phone number, or physical location. Describe who visitors should ' +
                        'contact for partnership inquiries, questions, or support.</p>',
                },
                order: 1,
            },
            {
                type: 'cta-button',
                data: {
                    text: 'Email Us',
                    link: 'mailto:contact@example.org',
                    variant: 'accent',
                },
                order: 2,
            },
        ],
    },
];

/**
 * Deep-clone a template's blocks and re-sequence their `order` to a contiguous
 * 0..n-1 range. The returned blocks share no object references with the
 * template definition, so editing them after they are seeded into the draft
 * can never mutate the template.
 *
 * Uses `structuredClone` where available (all supported browsers / Node 17+),
 * falling back to a JSON round-trip: block `data` is plain JSON-safe data, so
 * the fallback is lossless.
 *
 * @param {string} templateId - The `id` of a template in `CONTENT_TEMPLATES`.
 * @returns {Array<{type: string, data: object, order: number}>} A fresh,
 *   re-sequenced block list, or an empty array if the id is unknown.
 */
export const getTemplateBlocks = (templateId) => {
    const template = CONTENT_TEMPLATES.find((t) => t.id === templateId);
    if (!template) {
        return [];
    }
    const clone =
        typeof structuredClone === 'function'
            ? structuredClone(template.blocks)
            : JSON.parse(JSON.stringify(template.blocks));
    // Each template block gets a fresh `_clientId` so the editor keys it by
    // a stable identity (review finding 6). `_clientId` is a client-only
    // React key, never persisted server-side.
    return clone.map((block, index) => ({
        ...block,
        order: index,
        _clientId: newBlockClientId(),
    }));
};
